import * as styles from "./PdfViewerSearch.css";

import { type JSX, useEffect, useState } from "react";

import { useSearch } from "@embedpdf/plugin-search/react";
import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import { Spinner } from "@frontend/components/atoms/Spinner";
import { useLingui } from "@lingui/react/macro";

import { usePdfViewerContext } from "./PdfViewer.context";

export function PdfViewerSearch(): JSX.Element {
  const { t } = useLingui();

  const { documentId, setSearchOpen } = usePdfViewerContext();
  const { provides: search, state } = useSearch(documentId);

  const [query, setQuery] = useState(state.query);

  useEffect(() => {
    search?.startSearch();

    return () => search?.stopSearch();
  }, [search]);

  const submit = () => {
    if (search === null || query.trim() === "") {
      return;
    }

    search.searchAllPages(query);
  };

  const hasResults = state.total > 0;

  const position = state.activeResultIndex + 1;

  return (
    <div className={styles.root} data-slot="pdf-viewer-search">
      <Input
        autoFocus
        size="sm"
        className={styles.field}
        placeholder={t`Search in the document`}
        aria-label={t`Search in the document`}
        value={query}
        onValueChange={setQuery}
        leftSection={<Icon name="Search" size="sm" />}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            submit();
          }

          if (event.key === "Escape") {
            setSearchOpen(false);
          }
        }}
      />

      {state.loading ? (
        <Spinner />
      ) : (
        <span className={styles.count}>
          {hasResults
            ? t`${position} of ${state.total}`
            : state.query === ""
              ? ""
              : t`No result`}
        </span>
      )}

      <Button
        size="sm"
        variant="ghost"
        aria-label={t`Previous result`}
        disabled={!hasResults}
        onClick={() => search?.previousResult()}
      >
        <Icon name="ChevronUp" size="sm" />
      </Button>

      <Button
        size="sm"
        variant="ghost"
        aria-label={t`Next result`}
        disabled={!hasResults}
        onClick={() => search?.nextResult()}
      >
        <Icon name="ChevronDown" size="sm" />
      </Button>

      <Button
        size="sm"
        variant="ghost"
        aria-label={t`Close the search`}
        onClick={() => setSearchOpen(false)}
      >
        <Icon name="X" size="sm" />
      </Button>
    </div>
  );
}
